import { useTranslation } from 'react-i18next';
import { ToastLinks } from '../contexts/toastMessageContext';
import { IExecutionStatusStorage } from '../data-types/IExecutionStatusStorage';
import { TaxReturnStatusSignalREvent } from '../data-types/SignalREvent';
import { CommandType } from '../utils/EventGrid.constants';
import { useTaxReturnCalculationState } from './useTaxReturnCalculationState';
import { useToastMessage } from './useToastMessage';

export const getIgnoredMessages = (): Record<string, boolean> => {
    return {
        'No rights to post amounts.': true,
    };
};

const formatErrorMessageWithBoldReturnName = (errorMessage: string, returnName: string): React.ReactNode => {
    // Only format if we have both values and the return name is in the error message
    if (!returnName || !errorMessage || !errorMessage.includes(returnName)) {
        return errorMessage;
    }

    const parts = errorMessage.split(returnName);

    return (
        <>
            {parts[0]}
            <strong>{returnName}</strong>
            {parts.slice(1).join(returnName)}
        </>
    );
};

export const useEventsToastMessages = (): {
    checkForErrorsAndShowToast: (event: TaxReturnStatusSignalREvent, taxReturnStatus: IExecutionStatusStorage | undefined) => void;
} => {
    const { t } = useTranslation();
    const ignoredMessages = getIgnoredMessages();
    const { showToast } = useToastMessage();
    const { rerunCalculationReport } = useTaxReturnCalculationState();

    const showErrorToast = (message: string | React.ReactNode = t('anErrorOccurred'), links: ToastLinks[] = []) => {
        showToast(t('error'), message, 'error', links);
    };

    const onRetryClick = (taxReturnStatus: IExecutionStatusStorage | undefined, event: TaxReturnStatusSignalREvent) => {
        if (taxReturnStatus?.taxReturnItemKey && event?.notificationData?.ObjectId) {
            rerunCalculationReport(taxReturnStatus.taxReturnItemName, taxReturnStatus.taxReturnItemKey, event.notificationData?.ObjectId);
        }
    };

    const checkForErrorsAndShowToast = (event: TaxReturnStatusSignalREvent, taxReturnStatus: IExecutionStatusStorage | undefined) => {
        const links: ToastLinks[] = [];

        if (taxReturnStatus?.taxReturnItemKey && event.notificationData?.ObjectId) {
            links.push({
                linkProps: {
                    onClick: () => onRetryClick(taxReturnStatus, event),
                },
                text: t('retry'),
            });
        }

        if (event.errors && event.notificationData?.CommandType === CommandType.Execute) {
            for (const error of event.errors) {
                // Skip showing toast if error message is in the ignored list
                if (error.message && ignoredMessages[error.message]) {
                    continue;
                }

                // Only show messages with severity error
                if (error.error === true) {
                    const returnName = taxReturnStatus?.taxReturnItemName ?? '';
                    const errorMessage = error.message ?? '';
                    const formattedErrorMessage = formatErrorMessageWithBoldReturnName(errorMessage, returnName);

                    showErrorToast(<span>{formattedErrorMessage}</span>, links);
                }
            }
        }
    };

    return {
        checkForErrorsAndShowToast,
    };
};
